/**
 * Services Catalog
 *
 * GET /api/public/services (contract v1 §3.2) merged by slug with the
 * static marketing copy in data/services.js. The server wins for price,
 * duration, currency, name and bookable_online; the static file only
 * supplies the descriptive copy (title, short, long).
 *
 * The merged catalog is cached in memory for 5 minutes.
 */

import { get } from './apiClient';
import { SERVICES as STATIC_SERVICES } from '../../data/services';
import { formatDurationMinutes, getServiceDuration } from '../utils/serviceMapper';

export const SERVICES_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Slugs treated as not bookable online when the API cannot be reached and
 * the static entry has no `bookableOnline` flag. Empty since the Creative
 * Unwind group was retired (November 2026 catalog). When the API answers,
 * its `bookable_online` flag is used instead.
 */
export const FALLBACK_NOT_BOOKABLE_ONLINE = Object.freeze([]);

/**
 * Slugs never shown or booked on the site, even if the API still lists
 * them. The free 15-minute consultation was replaced by "Request a call"
 * (her November 2026 corrections); the backend no longer offers it online.
 */
export const HIDDEN_SERVICE_SLUGS = Object.freeze(['consultation']);

/**
 * Normalize one API service (snake_case) to the frontend shape.
 * @param {object} s - Item of data.services
 * @returns {{slug:string,name:string,durationMinutes:number,fee:number,currency:string,bookableOnline:boolean}}
 */
export const normalizeApiService = (s) => ({
  slug: s.slug,
  name: s.name,
  durationMinutes: s.duration_minutes,
  fee: s.fee,
  currency: s.currency,
  bookableOnline: s.bookable_online === true,
});

/**
 * Fetch the active services from the API (not cached).
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array>} Normalized services in display order
 */
export const fetchPublicServices = async ({ signal } = {}) => {
  const data = await get('/api/public/services', { signal });
  const services = Array.isArray(data.services) ? data.services : [];
  return services.filter((s) => s && typeof s.slug === 'string').map(normalizeApiService);
};

/**
 * Static entry used when the API is unreachable.
 * @param {object} staticService - Entry of data/services.js
 * @returns {object}
 */
const fromStatic = (staticService) => ({
  ...staticService,
  name: staticService.title,
  fee: staticService.price,
  currency: null,
  durationMinutes: Number.isFinite(staticService.durationMinutes)
    ? staticService.durationMinutes
    : getServiceDuration(staticService.duration),
  bookableOnline:
    typeof staticService.bookableOnline === 'boolean'
      ? staticService.bookableOnline
      : !FALLBACK_NOT_BOOKABLE_ONLINE.includes(staticService.slug),
  source: 'static',
});

/**
 * Merge API services with the static marketing copy.
 *
 * - Order follows the API (its display order).
 * - A service only the API knows about is included with its name as title.
 * - A static service the API does not list is inactive: it is kept for its
 *   marketing page but marked bookableOnline false.
 * - HIDDEN_SERVICE_SLUGS (the old free consultation) are left out.
 *
 * @param {Array|null} apiServices - Output of fetchPublicServices, or null when unavailable
 * @param {Array} [staticServices] - data/services.js SERVICES
 * @returns {Array} Merged services
 */
export const mergeServiceCatalog = (apiServices, staticServices = STATIC_SERVICES) => {
  if (!Array.isArray(apiServices)) {
    return staticServices.filter((s) => !HIDDEN_SERVICE_SLUGS.includes(s.slug)).map(fromStatic);
  }

  const staticBySlug = new Map(staticServices.map((s) => [s.slug, s]));
  const merged = apiServices.filter((api) => !HIDDEN_SERVICE_SLUGS.includes(api.slug)).map((api) => {
    const copy = staticBySlug.get(api.slug) || {};
    return {
      ...copy,
      slug: api.slug,
      title: copy.title || api.name,
      name: api.name,
      price: api.fee,
      fee: api.fee,
      currency: api.currency,
      durationMinutes: api.durationMinutes,
      duration: formatDurationMinutes(api.durationMinutes),
      bookableOnline: api.bookableOnline,
      source: 'api',
    };
  });

  const listed = new Set(apiServices.map((s) => s.slug));
  staticServices
    .filter((s) => !listed.has(s.slug) && !HIDDEN_SERVICE_SLUGS.includes(s.slug))
    .forEach((s) => merged.push({ ...fromStatic(s), bookableOnline: false, source: 'static-inactive' }));

  return merged;
};

let cache = null; // { services, fetchedAt }
let inFlight = null;

/**
 * Merged catalog, cached for SERVICES_CACHE_TTL_MS. Concurrent callers
 * share one request. Rejects with ApiError when the API fails; callers
 * can fall back to getStaticServiceCatalog().
 *
 * @param {object} [options]
 * @param {boolean} [options.force=false] - Bypass the cache
 * @returns {Promise<Array>}
 */
export const getServiceCatalog = async ({ force = false } = {}) => {
  if (!force && cache && Date.now() - cache.fetchedAt < SERVICES_CACHE_TTL_MS) {
    return cache.services;
  }
  if (!inFlight) {
    const request = fetchPublicServices()
      .then((apiServices) => {
        const services = mergeServiceCatalog(apiServices);
        if (inFlight === request) cache = { services, fetchedAt: Date.now() };
        return services;
      })
      .finally(() => {
        if (inFlight === request) inFlight = null;
      });
    inFlight = request;
  }
  return inFlight;
};

/**
 * Cached catalog if still fresh, else null. Lets hooks render without a
 * loading flash.
 * @returns {Array|null}
 */
export const getCachedServiceCatalog = () =>
  cache && Date.now() - cache.fetchedAt < SERVICES_CACHE_TTL_MS ? cache.services : null;

/**
 * Static-only catalog (API unreachable).
 * @returns {Array}
 */
export const getStaticServiceCatalog = () => mergeServiceCatalog(null);

/** Drop the cached catalog and forget any in-flight request. */
export const clearServiceCatalogCache = () => {
  cache = null;
  inFlight = null;
};
